/**
 * aggregator-list.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { Group } from "../auth/identity/group";
import { UserService } from "../auth/identity/user.service";
import { Rule } from "../auth/permissions/rules";

import { Aggregation } from "./aggregation";
import { AggregationService } from "./aggregation.service";
import { Aggregator } from "./aggregator";
import { DataExport } from "./data-export";
import { Exporter } from "./exporter";

@Component({
    selector: "aggregator-list",
    template: `
        <div class="form-group">
            <label>Algorithm</label>
            <select
                [formControl]="algorithmSelector"
                class="form-control"
                [size]="algorithms.all?.length || 1">
                <option
                    *ngFor="let algorithm of algorithms.all"
                    [value]="algorithm.name">
                    {{algorithm.displayName}}
                </option>
            </select>
        </div>
        <div class="form-group">
            <label>Group</label>
            <select
                [formControl]="groupSelector"
                class="form-control"
                [size]="(user.self?.groups?.length || 1)">
                <template
                    ngFor
                    let-group
                    [ngForOf]="user.self?.groups">
                    <option
                        *ngIf="group.allows(aggRule) || group.allows(expRule)"
                        [ngValue]="group"
                        [title]="permissionTooltip(group)">
                        {{group.name}}
                        {{!group.allows(aggRule) ? "(no analyses)" : ""}}
                        {{!group.allows(expRule) ? "(no data exports)" : ""}}
                    </option>
                    <option
                        *ngIf="!group.allows(aggRule) && !group.allows(expRule)"
                        title="You don't have permission to run any tools on this group."
                        disabled>
                        {{group.name}} (no tools available)
                    </option>
                </template>
            </select>
        </div>
        <div class="form-group" #selector>
            <label>Tool</label>
            <select class="tools form-control" [size]="toolCount()" [formControl]="toolSelector">
                <option disabled class="text-center">
                    Analysis
                </option>
                <option *ngIf="!service.aggregators.length" disabled default>
                    «no tools available»
                </option>
                <option
                    *ngFor="let aggregator of service.aggregators"
                    (click)="selector.target = aggregator"
                    (dblclick)="start(aggregator)"
                    [disabled]="aggsDisabled">
                    {{aggregator.name}}
                </option>
                <option disabled class="text-center">
                    Export
                </option>
                <option *ngIf="!service.exporters.length" disabled>
                «no tools available»
                </option>
                <option
                    *ngFor="let exporter of service.exporters"
                    (click)="selector.target = exporter"
                    (dblclick)="start(exporter)"
                    [disabled]="exportsDisabled">
                    {{exporter.displayName}}
                </option>
            </select>
            <br>
            <div class="description">
                <button
                    role="button"
                    class="btn btn-xs btn-primary pull-right"
                    (click)="start(selector.target)"
                    [disabled]="!selector.target">
                    Create query
                    <span class="glyphicon glyphicon-chevron-right"></span>
                </button>
                <template [ngIf]="selector.target">
                    <label>
                        {{selector.target.displayName || selector.target.name}}
                    </label>
                    <p>{{selector.target.description}}</p>
                </template>
                <em *ngIf="!selector.target">
                    Select a tool to view description.
                </em>
            </div>
        </div>
    `,
    styles: [
        `.tools option[disabled] {
            background-color: #f0f0f0;
        }
        .description {
            min-height: 200px;
        }`
    ]
})
export class AggregatorListComponent implements OnInit
{
    constructor (
        public service : AggregationService,
        public algorithms : AlgorithmService,
        public user : UserService
    ) { /* DI */ }

    public algorithmSelector : FormControl;
    public groupSelector : FormControl;
    public toolSelector : FormControl = new FormControl();
    public form : FormGroup;
    public readonly aggRule : Rule = Rule.RUN_AGGREGATOR;
    public readonly expRule : Rule = Rule.RUN_DATA_EXPORT;

    public toolCount () : number
    {
        return (
            2
            + (this.service.aggregators.length || 1)
            + (this.service.exporters.length || 1)
        );
    }

    public permissionTooltip (group : Group) : string
    {
        if (!group.allows(this.expRule))
        {
            return (
                `You do not have permission to run data exports on this group`);
        }
        else if (!group.allows(this.aggRule))
        {
            return (
                `You do not have permission to run aggregators on this group`);
        }
        else
        {
            return `You can run all tools on this group`;
        }
    }

    public exportsDisabled : boolean = false;
    public aggsDisabled : boolean = false;
    protected evaluatePermissions (group : Group) : void
    {
        if (group)
        {
            this.aggsDisabled = !group.allows(this.aggRule);
            this.exportsDisabled = !group.allows(this.expRule);
        } else {
            this.aggsDisabled = true;
            this.exportsDisabled = true;
        }
        this.toolSelector.reset();
    }

    public ngOnInit () : void
    {
        this.algorithmSelector = new FormControl(
            (this.algorithms.all[0] || {name: null}).name,
            Validators.required
        );
        this.groupSelector = new FormControl(
            this.user.self.groups.find((g : Group) => g.allows([this.aggRule, this.expRule], true)),
            Validators.required
        );
        if (!this.groupSelector.value) {
            console.warn("Could not find any groups with tools permissions");
        }
        this.evaluatePermissions(this.groupSelector.value);
        this.groupSelector.valueChanges.subscribe(
            (g : Group) => this.evaluatePermissions(g));
        this.form = new FormGroup({
            algorithm: this.algorithmSelector,
            group: this.groupSelector
        });
    }

    public start (tool : Aggregator | Exporter) : void
    {
        if (this.form.invalid)
        {
            return alert("You must select an algorithm and group first!");
        }

        this.service.startConfiguration(
            tool,
            this.algorithmSelector.value,
            this.groupSelector.value
        );
        window.scrollTo(0, 0);
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/aggregator-list.component.ts